import { getBlob, getMetadata, ref, uploadBytes, type UploadResult } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { functions, storage } from '../firebase';
import { PRODUCT_FEEDBACK_LIMITS } from '../types/productFeedback';

export interface ProductFeedbackAttachmentFinalizeResult {
  reportId: string;
  storagePath: string;
  generation: string;
  bytes: number;
}

export interface ProductFeedbackAttachmentUploadAuthorizationResult {
  reportId: string;
}

export type ProductFeedbackAttachmentObjectState = 'exists' | 'missing';

export const getProductFeedbackAttachmentPath = (reportId: string): string => {
  if (!/^[A-Za-z0-9_-]{8,128}$/.test(reportId)) throw new Error('Niepoprawny reportId.');
  return `product-feedback-reports/${reportId}/screenshot.jpg`;
};

export const authorizeProductFeedbackAttachmentUpload = async (
  reportId: string
): Promise<ProductFeedbackAttachmentUploadAuthorizationResult> => {
  getProductFeedbackAttachmentPath(reportId);
  const callable = httpsCallable<{ reportId: string }, unknown>(
    functions,
    'authorizeProductFeedbackAttachmentUpload'
  );
  const result = await callable({ reportId });
  const data = result.data as Record<string, unknown>;
  if (!data || Object.keys(data).length !== 1 || data.reportId !== reportId) {
    throw new Error('Autoryzacja wysyłania załącznika zwróciła niespójną odpowiedź.');
  }
  return { reportId };
};

export const uploadProductFeedbackScreenshot = async (
  reportId: string,
  canonicalJpeg: Blob
): Promise<UploadResult> => {
  if (canonicalJpeg.type !== 'image/jpeg') throw new Error('Załącznik canonical musi być JPEG.');
  if (canonicalJpeg.size > PRODUCT_FEEDBACK_LIMITS.maxAttachmentBytes) {
    throw new Error('Załącznik canonical przekracza 1 MiB.');
  }
  return uploadBytes(ref(storage, getProductFeedbackAttachmentPath(reportId)), canonicalJpeg, {
    contentType: 'image/jpeg',
  });
};

export const inspectProductFeedbackAttachmentObject = async (
  reportId: string
): Promise<ProductFeedbackAttachmentObjectState> => {
  try {
    await getMetadata(ref(storage, getProductFeedbackAttachmentPath(reportId)));
    return 'exists';
  } catch (error) {
    if (String((error as { code?: unknown })?.code || '').includes('storage/object-not-found')) {
      return 'missing';
    }
    throw error;
  }
};

export const readProductFeedbackScreenshot = async (reportId: string): Promise<Blob> => {
  const blob = await getBlob(ref(storage, getProductFeedbackAttachmentPath(reportId)));
  if (blob.type !== 'image/jpeg' || blob.size > PRODUCT_FEEDBACK_LIMITS.maxAttachmentBytes) {
    throw new Error('Odczytany załącznik nie spełnia canonical kontraktu JPEG.');
  }
  return blob;
};

export const finalizeProductFeedbackAttachment = async (
  reportId: string
): Promise<ProductFeedbackAttachmentFinalizeResult> => {
  const callable = httpsCallable<{ reportId: string }, unknown>(
    functions,
    'finalizeProductFeedbackAttachment'
  );
  const result = await callable({ reportId });
  const data = result.data as Record<string, unknown>;
  if (
    data?.reportId !== reportId ||
    data.storagePath !== getProductFeedbackAttachmentPath(reportId) ||
    typeof data.generation !== 'string' ||
    !data.generation ||
    !Number.isInteger(data.bytes) ||
    Number(data.bytes) < 3 ||
    Number(data.bytes) > PRODUCT_FEEDBACK_LIMITS.maxAttachmentBytes
  ) {
    throw new Error('Finalizacja załącznika zwróciła niespójną odpowiedź.');
  }
  return data as unknown as ProductFeedbackAttachmentFinalizeResult;
};
